import React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Icons,
  Modal,
  Text,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import formatDate from '@core/utils/formatDate';
import getSection from '@core/materials/api/getSection';
import SectionContent from '../../MaterialResponsive/MaterialEditor/SectionView/SectionContent';
import { MaterialViewSkeleton } from '../../MaterialResponsive/Common/Helper';
import { createStyles } from './MaterialPreviewModal.styles';
import Skeleton from '@mui/material/Skeleton';

export type MaterialPreviewData = {
  title: string;
  lastEditedDate: string;
  content: any;
  pageUrl?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onGoToPage: () => void;
  spaceId?: string;
  materialId?: string | null;
  previewTitle?: string | null;
};

const MaterialPreviewModal = ({
  open,
  onClose,
  onGoToPage,
  spaceId,
  materialId,
  previewTitle,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [material, setMaterial] = useState<MaterialPreviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open || !spaceId || !materialId) {
      return;
    }

    let active = true;
    setIsLoading(true);
    setMaterial(null);

    void getSection(spaceId, materialId, false)
      .then((section) => {
        if (!active) return;
        setMaterial({
          title: section.title || '',
          lastEditedDate: formatDate(
            section.updatedAt || section.createdAt || ''
          ),
          content: section.content,
          pageUrl: `/dashboard/${spaceId}/journey/materials/${materialId}`,
        });
      })
      .catch(() => {
        if (!active) return;
        setMaterial(null);
      })
      .finally(() => {
        if (!active) return;
        setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, spaceId, materialId]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="material-preview-title"
      aria-modal="true"
    >
      <Box sx={styles.container} role="dialog">
        {/* Header */}
        <Box sx={styles.header}>
          <Box sx={styles.closeRow}>
            <IconButton
              size="small"
              aria-label="Close material preview"
              onClick={onClose}
              sx={styles.closeButton}
            >
              <Tooltip
                title="Close"
                disableFocusListener={!isMdUp}
                disableHoverListener={!isMdUp}
                disableTouchListener={!isMdUp}
                position="top"
              >
                <Icons.Close aria-hidden />
              </Tooltip>
            </IconButton>
          </Box>
          <Text id="material-preview-title" sx={styles.title}>
            {previewTitle || material?.title}
          </Text>
          {isLoading ? (
            <Skeleton variant="text" width={180} height={34} />
          ) : material?.lastEditedDate ? (
            <Text sx={styles.lastEdited}>
              Last edited on {material.lastEditedDate}
            </Text>
          ) : null}
        </Box>

        {/* Scrollable content */}
        <Box sx={styles.content}>
          {isLoading ? (
            <Box sx={styles.contentSkeleton}>
              <MaterialViewSkeleton
                showTitle={false}
                showButtons={false}
                count={6}
              />
            </Box>
          ) : (
            <SectionContent
              editable={false}
              sectionId={materialId || ''}
              content={material?.content}
              isLoadingContent={false}
            />
          )}
        </Box>

        {/* Footer */}
        <Box sx={styles.footer}>
          <Button
            kind="secondary"
            onClick={onGoToPage}
            sx={styles.goToPageButton}
          >
            Go to the page
            <Icons.ArrowForwardIos sx={{ fontSize: '14px' }} />
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default MaterialPreviewModal;
